import { useState } from 'react';
import styles from '../assets/css/member-registration.module.css';
import { basicInfoFields, emergencyFields, familyFields } from '../assets/js/confirmFields';
import { postalService } from '../services/postalService';
import { toWareki } from '../utils/wareki';

const placeholderMap = {
  name: "例: 山田　太郎",
  furigana: "例: ヤマダ　タロウ",
  birthdate: "",
  postal_code: "例: 107-0052",
  address_resident: "例: 東京都港区赤坂1-1-1",
  postal_code_post: "例: 107-0052",
  address_post: "例: 東京都港区赤坂1-1-1",
  nearest_station: "例: 東京メトロ千代田線 赤坂駅",
  phone: "例: 090-1234-5678",
  blood_type: "例: A型",
  bank_name: "例: 三井住友銀行",
  branch_name: "例: 赤坂支店",
  account_number: "例: 1234567",
  emergency_name: "例: 山田　花子",
  emergency_furigana: "例: ヤマダ　ハナコ",
  emergency_relationship: "例: 妻",
  emergency_phone: "例: 090-1234-5678",
};

// 郵便番号キー → 自動入力先の住所キー
const postalTargetMap = {
  postal_code: 'address_resident',
  postal_code_post: 'address_post',
};

function renderInputField({ key, label, required, type = 'text', placeholder, readOnly }, value, onChange) {
  return (
    <div className="mb-3" key={key}>
      <label className="form-label">
        {label} {required && <span className="text-danger">（必須）</span>}
        {readOnly && <small className="text-muted ms-2">（アカウント情報から自動反映）</small>}
      </label>
      {type === 'textarea' ? (
        <textarea
          name={key}
          className="form-control"
          value={value || ''}
          onChange={onChange}
          placeholder={placeholder || ''}
          rows="3"
          autoComplete="off"
          readOnly={readOnly}
          style={readOnly ? {backgroundColor: '#e9ecef'} : undefined}
        />
      ) : (
        <input
          type={type}
          name={key}
          className="form-control"
          value={value || ''}
          onChange={onChange}
          placeholder={placeholder || ''}
          autoComplete="off"
          readOnly={readOnly}
          style={readOnly ? {backgroundColor: '#e9ecef'} : undefined}
        />
      )}
    </div>
  );
}

function InputForm({ formData, familyMembers, errors, onInputChange, onFamilyChange, onAddFamilyMember, onRemoveFamilyMember, onSubmit, readOnlyFields = [] }) {
  // 郵便番号検索の状態（キーごとに loading / error を保持）
  const [postalState, setPostalState] = useState({});

  const handlePostalSearch = async (key) => {
    const targetKey = postalTargetMap[key];
    const zipcode = (formData[key] || '').trim();
    if (!/^\d{3}-?\d{4}$/.test(zipcode)) {
      setPostalState(prev => ({ ...prev, [key]: { loading: false, error: '郵便番号は7桁で入力してください（例: 107-0052）。' } }));
      return;
    }
    setPostalState(prev => ({ ...prev, [key]: { loading: true, error: '' } }));
    try {
      const data = await postalService.search(zipcode);
      onInputChange({ target: { name: targetKey, value: data.address } });
      setPostalState(prev => ({ ...prev, [key]: { loading: false, error: '' } }));
    } catch (err) {
      setPostalState(prev => ({
        ...prev,
        [key]: { loading: false, error: err?.data?.error || '住所が見つかりませんでした。' }
      }));
    }
  };

  const showFamilySection = formData.has_dependents === '有';

  return (
    <>
      {/* 基本情報 */}
      <section className="mb-5">
        <h2 className={styles["section-title"]}>基本情報</h2>
        {basicInfoFields.map(({ key, label, required }) => {
          // 血液型はセレクトボックス
          if (key === "blood_type") {
            return (
              <div className="mb-3" key={key}>
                <label className="form-label">
                  {label} {required && <span className="text-danger">（必須）</span>}
                </label>
                <select
                  name={key}
                  className="form-control"
                  value={formData[key] || ''}
                  onChange={onInputChange}
                >
                  <option value="">選択してください</option>
                  <option value="A">A</option>
                  <option value="B">B</option>
                  <option value="O">O</option>
                  <option value="AB">AB</option>
                </select>
              </div>
            );
          }
          // 口座種別はセレクトボックス
          if (key === "account_type") {
            return (
              <div className="mb-3" key={key}>
                <label className="form-label">
                  {label} {required && <span className="text-danger">（必須）</span>}
                </label>
                <select
                  name={key}
                  className="form-control"
                  value={formData[key] || ''}
                  onChange={onInputChange}
                >
                  <option value="">選択してください</option>
                  <option value="普通">普通</option>
                  <option value="当座">当座</option>
                </select>
              </div>
            );
          }
          // 郵便番号は住所検索ボタン付き
          if (key === "postal_code" || key === "postal_code_post") {
            const state = postalState[key] || {};
            return (
              <div className="mb-3" key={key}>
                <label className="form-label">
                  {label} {required && <span className="text-danger">（必須）</span>}
                </label>
                <div className="d-flex" style={{ gap: 8 }}>
                  <input
                    type="text"
                    name={key}
                    className="form-control"
                    value={formData[key] || ''}
                    onChange={onInputChange}
                    placeholder={placeholderMap[key]}
                    autoComplete="off"
                    style={{ maxWidth: 200 }}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => handlePostalSearch(key)}
                    disabled={state.loading}
                  >
                    {state.loading ? '検索中...' : '住所検索'}
                  </button>
                </div>
                <small className="text-muted">郵便番号を入力して「住所検索」を押すと住所が自動入力されます。番地以降は追記してください。</small>
                {state.error && <div className="text-danger" style={{ fontSize: '0.85rem' }}>{state.error}</div>}
              </div>
            );
          }
          // 生年月日は和暦を自動表示
          if (key === "birthdate") {
            const isReadOnly = readOnlyFields.includes(key);
            return (
              <div className="mb-3" key={key}>
                <label className="form-label">
                  {label} {required && <span className="text-danger">（必須）</span>}
                </label>
                <div className="d-flex align-items-center" style={{ gap: 12 }}>
                  <input
                    type="date"
                    name={key}
                    className="form-control"
                    value={formData[key] || ''}
                    onChange={isReadOnly ? undefined : onInputChange}
                    autoComplete="off"
                    readOnly={isReadOnly}
                    style={{ maxWidth: 200, ...(isReadOnly ? { backgroundColor: '#e9ecef' } : {}) }}
                  />
                  {formData[key] && toWareki(formData[key]) && (
                    <span className="text-muted">{toWareki(formData[key])}</span>
                  )}
                </div>
              </div>
            );
          }
          // 住所だけtextareaにする
          const type = (key === "address_resident" || key === "address_post") ? "textarea" : "text";
          const isReadOnly = readOnlyFields.includes(key);
          return renderInputField({ key, label, required, type, placeholder: placeholderMap[key], readOnly: isReadOnly }, formData[key], isReadOnly ? undefined : onInputChange);
        })}
      </section>

      {/* 緊急連絡先 */}
      <section className="mb-5">
        <h2 className={styles["section-title"]}>緊急連絡先情報</h2>
        {emergencyFields.map(({ key, label, required }) =>
          renderInputField({ key, label, required, type: "text", placeholder: placeholderMap[key] }, formData[key], onInputChange)
        )}
      </section>

      {/* 扶養家族 */}
      <section className="mb-5">
        <h2 className={styles["section-title"]}>扶養家族情報</h2>
        <div className="mb-3">
          <label className="form-label">
            扶養家族の有無 <span className="text-danger">（必須）</span>
          </label>
          <select
            name="has_dependents"
            className="form-control"
            value={formData.has_dependents || ''}
            onChange={onInputChange}
            style={{ maxWidth: 200 }}
          >
            <option value="">選択してください</option>
            <option value="有">有</option>
            <option value="無">無</option>
          </select>
        </div>
        {showFamilySection && (
          <>
            <p className="text-muted" style={{ fontSize: '0.9rem' }}>
              扶養家族の情報をすべてご記入ください。配偶者の場合は年収区分も必須です。
            </p>
            <div id="family-members">
              {familyMembers.map((member, index) => (
                <div key={index} className="mb-3 border p-3 rounded">
                  <div className="d-flex justify-content-between align-items-center">
                    <label className="form-label mb-0">扶養家族 {index + 1}</label>
                    {onRemoveFamilyMember && familyMembers.length > 1 && (
                      <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => onRemoveFamilyMember(index)}>削除</button>
                    )}
                  </div>
                  {familyFields.map(({ key, label, required }) => {
                    if (key === 'relationship') {
                      const presets = ['配偶者', '子', '父', '母', '祖父', '祖母', '兄弟姉妹'];
                      const currentValue = member[key] || '';
                      const isOther = currentValue && !presets.includes(currentValue);
                      return (
                        <div className="mb-2" key={key}>
                          <label className="form-label">{label}</label>
                          <select
                            name={key}
                            className="form-control"
                            value={isOther ? 'その他' : currentValue}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === 'その他') {
                                onFamilyChange(index, { target: { name: key, value: '' } });
                                onFamilyChange(index, { target: { name: '_relationshipOther', value: true } });
                              } else {
                                onFamilyChange(index, { target: { name: '_relationshipOther', value: false } });
                                onFamilyChange(index, e);
                              }
                            }}
                          >
                            <option value="">選択してください</option>
                            <option value="配偶者">配偶者</option>
                            <option value="子">子</option>
                            <option value="父">父</option>
                            <option value="母">母</option>
                            <option value="祖父">祖父</option>
                            <option value="祖母">祖母</option>
                            <option value="兄弟姉妹">兄弟姉妹</option>
                            <option value="その他">その他</option>
                          </select>
                          {(isOther || member._relationshipOther) && (
                            <input
                              type="text"
                              name={key}
                              className="form-control mt-1"
                              placeholder="続柄を入力してください"
                              value={isOther ? currentValue : ''}
                              onChange={(e) => onFamilyChange(index, e)}
                            />
                          )}
                        </div>
                      );
                    }
                    if (key === 'cohabitation') {
                      return (
                        <div className="mb-2" key={key}>
                          <label className="form-label">{label}</label>
                          <select
                            name={key}
                            className="form-control"
                            value={member[key] || ""}
                            onChange={(e) => onFamilyChange(index, e)}
                          >
                            <option value="">選択してください</option>
                            <option value="同">同</option>
                            <option value="別">別</option>
                          </select>
                        </div>
                      );
                    }
                    if (key === 'income_range') {
                      if (member.relationship !== '配偶者') return null;
                      return (
                        <div className="mb-2" key={key}>
                          <label className="form-label">{label}（配偶者は必須）</label>
                          <select
                            name={key}
                            className="form-control"
                            value={member[key] || ""}
                            onChange={(e) => onFamilyChange(index, e)}
                          >
                            <option value="">選択してください</option>
                            <option value="58万以下">58万以下</option>
                            <option value="58～95万">58～95万</option>
                            <option value="95万以上">95万以上</option>
                          </select>
                        </div>
                      );
                    }
                    if (key === 'birthdate') {
                      return (
                        <div className="mb-2" key={key}>
                          <label className="form-label">{label}</label>
                          <div className="d-flex align-items-center" style={{ gap: 12 }}>
                            <input
                              type="date"
                              name={key}
                              className="form-control"
                              value={member[key] || ""}
                              onChange={(e) => onFamilyChange(index, e)}
                              style={{ maxWidth: 200 }}
                            />
                            {member[key] && toWareki(member[key]) && (
                              <span className="text-muted">{toWareki(member[key])}</span>
                            )}
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div className="mb-2" key={key}>
                        <label className="form-label">{label}</label>
                        <input
                          type="text"
                          name={key}
                          className="form-control"
                          value={member[key] || ""}
                          onChange={(e) => onFamilyChange(index, e)}
                        />
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
            <button type="button" onClick={onAddFamilyMember} className="btn btn-secondary btn-sm">
              扶養家族を追加
            </button>
          </>
        )}
      </section>
    </>
  );
}

export default InputForm;
